import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { coy } from "react-syntax-highlighter/dist/esm/styles/prism";

import CSVTableComponent from "./CSVTableComponent";
import TableComponent from "./TableComponent";
import { extractTableContent } from "../utils/csvParser";
import { preprocessMarkdown } from "../utils/markdownPreprocess";

const paragraphStyle = { fontSize: "0.9rem", lineHeight: "1.3rem" };
const listStyle = (listStyleType) => ({
  paddingLeft: "1.5rem",
  marginLeft: "0",
  listStyleType,
  marginBottom: "1rem",
});
const listItemStyle = {
  fontSize: "0.9rem",
  lineHeight: "1.5rem",
  marginTop: "0.25rem",
  marginBottom: "0.25rem",
  display: "list-item",
};
const nestedListItemStyle = {
  fontSize: "0.9rem",
  lineHeight: "1.3rem",
  marginTop: "0.5rem",
};

/**
 * Table placement marker emitted by the assistant: `[table:<table_id>]`.
 * The LLM never reproduces query-result rows — it emits this marker and the
 * table is rendered directly from the backend's structured payload
 * (message.tableMetadata[n].columns/rows), so the query result always
 * reaches the UI exactly as the SQL returned it.
 */
const TABLE_MARKER = /\[table:([A-Za-z0-9-]+)\]/g;
// A marker still streaming in (unclosed at end of content) — hidden until complete.
const PARTIAL_TABLE_MARKER = /\[table:[A-Za-z0-9-]*$/;

/**
 * Resolves the table_id (if any) from a hast code node's info-string meta and
 * returns the matching tableMetadata entry from the message. Per ADR 0001
 * we match by id rather than position so we're robust to multi-tool
 * aggregation and out-of-order metadata.
 */
const resolveTableMetadata = (node, message) => {
  const meta = node?.data?.meta || "";
  const idMatch = /\bid=([^\s]+)/.exec(meta);
  const tableId = idMatch ? idMatch[1] : null;

  if (!tableId) return null;
  return (
    message.tableMetadata?.find((m) => m && m.table_id === tableId) || null
  );
};

const renderCodeBlock = (message) =>
  function CodeRenderer({ node, inline, className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || "");
    const content = String(children).replace(/\n$/, "");

    // CSV-fenced block (legacy path — messages persisted before the
    // [table:id] marker contract) — render as a real, downloadable table.
    if (!inline && match?.[1] === "csv") {
      const metadata = resolveTableMetadata(node, message);
      return <CSVTableComponent csvContent={content} metadata={metadata} />;
    }

    // Markdown-fenced block (or a pipe table inside a fence) — re-parse
    // the inner content so the table renderer kicks in.
    if (
      !inline &&
      (match?.[1] === "markdown" || content.trim().startsWith("|"))
    ) {
      const tableContent = extractTableContent(content);
      return (
        <div>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              table: TableComponent,
              p: ({ children }) => <p style={paragraphStyle}>{children}</p>,
              li: ({ children }) => (
                <li style={nestedListItemStyle}>{children}</li>
              ),
            }}
          >
            {tableContent}
          </ReactMarkdown>
        </div>
      );
    }

    // Any other fenced code — syntax-highlight it.
    return !inline && match ? (
      <SyntaxHighlighter
        style={coy}
        language={match[1]}
        PreTag="div"
        {...props}
      >
        {content}
      </SyntaxHighlighter>
    ) : (
      <code className={className} {...props}>
        {children}
      </code>
    );
  };

/**
 * Splits assistant content on `[table:<id>]` markers into an ordered list of
 * `{ type: "md", text }` and `{ type: "table", id }` segments.
 */
const splitOnTableMarkers = (content) => {
  const segments = [];
  let last = 0;
  const re = new RegExp(TABLE_MARKER.source, "g");
  let m;
  while ((m = re.exec(content)) !== null) {
    if (m.index > last) {
      segments.push({ type: "md", text: content.slice(last, m.index) });
    }
    segments.push({ type: "table", id: m[1] });
    last = m.index + m[0].length;
  }
  if (last < content.length) {
    segments.push({ type: "md", text: content.slice(last) });
  }
  return segments;
};

/**
 * Wraps ReactMarkdown with the project's component overrides for code
 * blocks, tables, paragraphs, lists, and emphasis. Assistant content is
 * split on [table:id] markers; each marker renders the corresponding
 * backend-provided table (never LLM-reproduced data). Any structured table
 * the model failed to reference is appended after the prose once streaming
 * ends, guaranteeing every query result is shown.
 */
const MarkdownContent = ({ message }) => {
  const markdownComponents = {
    code: renderCodeBlock(message),
    table: TableComponent,
    p: ({ children }) => <p style={paragraphStyle}>{children}</p>,
    ul: ({ children }) => <ul style={listStyle("disc")}>{children}</ul>,
    ol: ({ children }) => <ol style={listStyle("decimal")}>{children}</ol>,
    li: ({ children }) => <li style={listItemStyle}>{children}</li>,
    strong: ({ children }) => (
      <strong style={{ display: "inline-block", marginBottom: "0.25rem" }}>
        {children}
      </strong>
    ),
  };

  // `steps` exists only while the agent run is streaming (useAgentRun
  // deletes it on every terminal path).
  const isStreaming = Boolean(message.steps?.length);

  let content = preprocessMarkdown(message.content || "");
  if (isStreaming) {
    content = content.replace(PARTIAL_TABLE_MARKER, "");
  }

  const segments = splitOnTableMarkers(content);
  const findMetadata = (id) =>
    message.tableMetadata?.find((m) => m && m.table_id === id) || null;

  // Safety net: structured tables the model never referenced (missing or
  // mangled marker, and no legacy csv fence for that id in the content).
  // Rendered after the prose so the query result is always visible.
  const referencedIds = new Set(
    segments.filter((s) => s.type === "table").map((s) => s.id),
  );
  const unreferencedTables = isStreaming
    ? []
    : (message.tableMetadata || []).filter(
        (m) =>
          m &&
          Array.isArray(m.rows) &&
          m.table_id &&
          !referencedIds.has(m.table_id) &&
          !content.includes(`id=${m.table_id}`),
      );

  return (
    <>
      {segments.map((seg, i) =>
        seg.type === "table" ? (
          findMetadata(seg.id) ? (
            <CSVTableComponent
              key={`t-${seg.id}-${i}`}
              metadata={findMetadata(seg.id)}
            />
          ) : null
        ) : (
          <ReactMarkdown
            key={`md-${i}`}
            remarkPlugins={[remarkGfm]}
            components={markdownComponents}
          >
            {seg.text}
          </ReactMarkdown>
        ),
      )}
      {unreferencedTables.map((m) => (
        <CSVTableComponent key={`u-${m.table_id}`} metadata={m} />
      ))}
    </>
  );
};

export default MarkdownContent;
